import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'
import { useAsync, useCart } from '../lib/store'
import { Fill, Img, Loading, ProductCard, Shell } from '../components/Layout'

/** `photo` is the design's `lum-c1`…`lum-c6` slot, in the design's own order. */
const CONCERN_TILES = [
  { label: 'Acne &\nblemishes', concern: 'Acne & blemishes', photo: 'skin-acne.jpg', alt: 'Close-up of blemish-prone skin on a cheek and jaw' },
  { label: 'Ageing &\nfine lines', concern: 'Ageing & fine lines', photo: 'skin-lines.jpg', alt: 'Close-up of fine lines around an outer eye' },
  { label: 'Dark spots &\npigmentation', concern: 'Dark spots & pigmentation', photo: 'skin-pigmentation.jpg', alt: 'Close-up of uneven pigmentation across a cheekbone' },
  { label: 'Dryness &\ndehydration', concern: 'Dryness & dehydration', photo: 'skin-dry.jpg', alt: 'Close-up of dry, dehydrated skin on a cheek' },
  { label: 'Sensitive\nskin', concern: 'Sensitive skin', photo: 'skin-sensitive.jpg', alt: 'Close-up of a cheek with a swatch of cream part-blended into reactive skin' },
  { label: 'Pores &\ntexture', concern: 'Pores & texture', photo: 'skin-pores.jpg', alt: 'Close-up of visible pores and uneven texture on a nose and inner cheek' },
]

/** The community row — the design's `lum-ug1`…`lum-ug4`. */
const COMMUNITY = [
  { photo: 'hands-pump-bottle.jpg', alt: 'A hand holding a white pump skincare bottle' },
  { photo: 'jar-gold-tray.jpg', alt: 'A white cosmetic jar on a brushed-gold tray beside folded linen' },
  { photo: 'applying-eyes-closed.jpg', alt: 'A woman applying moisturiser to her cheek, eyes closed' },
  { photo: 'dropper-silk.jpg', alt: 'A glass pipette dropper resting on draped pale beige silk' },
]

/**
 * “Seen on your For You page” — the design's `lum-v1`…`lum-v5`.
 *
 * Each entry is the poster frame. Give one a `video` (a URL under
 * `/assets/photos/`, or any media URL) and the tile plays it in the lightbox
 * instead of enlarging the still; until then the tile opens the photograph.
 */
const STILLS: { photo: string; alt: string; video?: string }[] = [
  { photo: 'bottle-by-cheek.jpg', alt: 'A woman holding a white skincare bottle beside her cheek' },
  { photo: 'drops-palm.jpg', alt: 'Three drops of clear serum dispensed onto an open palm' },
  { photo: 'cream-dots.jpg', alt: 'Cream dotted along a cheekbone and forehead mid-routine' },
  { photo: 'flatlay-four.jpg', alt: 'Four white skincare bottles on a beige towel, seen from above' },
  { photo: 'apply-cream-bathroom.jpg', alt: 'A woman smoothing cream along her cheek in bathroom daylight' },
]

/** The design's play affordance: a 42px scrim disc over the still. */
function PlayBadge() {
  return (
    <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
      <span style={{ width: 42, height: 42, borderRadius: '50%', background: 'rgba(28,25,23,.42)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="#fdfbf9" aria-hidden="true">
          <path d="M8 5l11 7-11 7V5Z" />
        </svg>
      </span>
    </span>
  )
}

/** Opens a still (or its video, once one is supplied) over the page. */
function StillLightbox({ still, onClose }: { still: (typeof STILLS)[number]; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [onClose])

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={still.alt}
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(28,25,23,.82)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'clamp(16px,4vw,56px)' }}
    >
      <button
        onClick={onClose}
        aria-label="Close"
        style={{ position: 'absolute', top: 16, right: 20, background: 'none', border: 'none', color: '#fdfbf9', fontSize: 26, lineHeight: 1, cursor: 'pointer', padding: 8 }}
      >
        ×
      </button>
      {still.video ? (
        <video src={still.video} poster={`/assets/photos/${still.photo}`} controls autoPlay playsInline style={{ maxWidth: '100%', maxHeight: '100%' }} />
      ) : (
        <img src={`/assets/photos/${still.photo}`} alt={still.alt} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
      )}
    </div>
  )
}

const PILLARS = [
  {
    title: 'Advanced ingredients',
    copy: 'Clinically studied actives chosen for real results.',
    path: 'M10.3 10.7 6.6 8.3M13.7 10.7l3.7-2.4M10.6 13.6 7.4 16.5M13.4 13.6l3.2 2.9',
    circles: true,
  },
  { title: 'Visible results', copy: 'Formulas designed to transform your skin.', path: 'M12 4c2 3 2 5.5 0 8-2-2.5-2-5 0-8ZM12 12c2.6-1.6 5-1.4 7 .6-2.4 1.8-4.8 1.7-7-.6ZM12 12c-2.6-1.6-5-1.4-7 .6 2.4 1.8 4.8 1.7 7-.6ZM12 12c1.7 2.4 1.8 4.8 0 7.4-1.8-2.6-1.7-5 0-7.4Z' },
  { title: 'Clean & conscious', copy: 'Thoughtful formulas. No unnecessary additives.', path: 'M12 4.5c3 3.6 4.8 6.1 4.8 8.4A4.8 4.8 0 0 1 12 19.5a4.8 4.8 0 0 1-4.8-6.6C7.2 10.6 9 8.1 12 4.5Z' },
  { title: 'Made for you', copy: 'Effective, gentle and suitable for all skin types.', path: 'M5 19c0-6.5 4.5-11 14-11 0 9.5-4.5 14-11 14-1.6 0-3-1.3-3-3ZM8.5 19C11 15 14 12.5 17.5 11' },
]

function ReviewCarousel() {
  const { data } = useAsync(() => api.featuredReviews(), [])
  const [i, setI] = useState(0)
  const reviews = data ?? []

  useEffect(() => {
    if (reviews.length < 2) return
    const t = setInterval(() => setI((n) => (n + 1) % reviews.length), 6000)
    return () => clearInterval(t)
  }, [reviews.length])

  if (!reviews.length) return null
  const r = reviews[i % reviews.length]

  return (
    <div style={{ padding: 'clamp(24px,3vw,44px)', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 16 }}>
      <h2 style={{ margin: 0, fontSize: 13, fontWeight: 500, letterSpacing: '.16em', textTransform: 'uppercase', lineHeight: 1.7 }}>
        Loved by the
        <br />
        Lumera community
      </h2>
      <div style={{ color: 'var(--rose-soft)', fontSize: 14, letterSpacing: '.24em' }}>{'★'.repeat(r.rating)}</div>
      <p key={i} className="fade" style={{ margin: 0, fontSize: 13, fontWeight: 300, lineHeight: 1.8, color: '#4b423b', minHeight: '5.4em' }}>
        “{r.body}”
      </p>
      <div style={{ fontSize: 12, fontWeight: 400, color: 'var(--muted)' }}>
        — {r.author}
        {r.location ? `, ${r.location}` : ''}
      </div>
      {reviews.length > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 6 }}>
          <button onClick={() => setI((n) => (n - 1 + reviews.length) % reviews.length)} aria-label="Previous review" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--muted)', fontSize: 14 }}>
            ‹
          </button>
          <span style={{ display: 'flex', gap: 7 }}>
            {reviews.map((_, n) => (
              <button
                key={n}
                onClick={() => setI(n)}
                aria-label={`Review ${n + 1}`}
                aria-current={n === i % reviews.length}
                style={{ width: 7, height: 7, borderRadius: '50%', border: '1px solid var(--ink)', padding: 0, cursor: 'pointer', background: n === i % reviews.length ? 'var(--ink)' : 'transparent' }}
              />
            ))}
          </span>
          <button onClick={() => setI((n) => (n + 1) % reviews.length)} aria-label="Next review" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--muted)', fontSize: 14 }}>
            ›
          </button>
        </div>
      )}
    </div>
  )
}

export default function Home() {
  const { add } = useCart()
  const { data: products, loading } = useAsync(() => api.products({ featured: true }), [])
  const { data: posts } = useAsync(() => api.posts(), [])
  const [playing, setPlaying] = useState<number | null>(null)

  const bestsellers = (products ?? []).slice(0, 8)
  const routine = ['acne-purifying-face-wash', 'bioactive-jelly-serum', 'acne-care-moisturiser', 'sunscreen-spf30-tint']
  const { data: routineProducts } = useAsync(() => api.products(), [])
  const routineSteps = routine
    .map((slug) => (routineProducts ?? []).find((p) => p.slug === slug))
    .filter(Boolean)
    .slice(0, 4)

  return (
    <Shell>
      {/* ---------------------------------------------------------- hero */}
      <section id="top" className="section-split" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.05fr) minmax(0,1fr)', background: 'var(--sand)', minHeight: 'clamp(420px,46vw,620px)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 22, padding: 'clamp(28px,5vw,80px)' }}>
          <div className="eyebrow">Advanced skincare, timeless beauty.</div>
          <h1 className="display h-hero" style={{ maxWidth: '15ch', textWrap: 'pretty' }}>
            High-performance skincare for luminous, healthy-looking skin.
          </h1>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 300, lineHeight: 1.7, color: '#463d36', maxWidth: '34ch' }}>
            Powerful ingredients. Purposeful formulas.
            <br />
            Made for every version of you.
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 6 }}>
            <Link to="/shop" className="btn btn-solid" style={{ padding: '14px 30px' }}>
              Shop Lumera
            </Link>
            <Link to="/about#philosophy" className="btn" style={{ padding: '14px 30px' }}>
              Discover Lumera
            </Link>
          </div>
        </div>
        <div style={{ position: 'relative', minHeight: 'clamp(300px,40vw,620px)' }}>
          <Fill src="/assets/photos/hero-portrait.jpg" alt="A woman with luminous, dewy skin against a warm beige backdrop" label="Hero portrait — glowing skin" />
        </div>
      </section>

      {/* --------------------------------------------------- bestsellers */}
      <section id="bestsellers" className="section">
        <h2 className="rule-head">Meet the Lumera icons</h2>
        {loading ? (
          <Loading label="Loading products" />
        ) : (
          <div className="grid-products">
            {bestsellers.map((p) => (
              <ProductCard key={p.slug} product={p} onAdd={() => add(p.slug, 1, p.name)} />
            ))}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 'clamp(14px,2vw,24px)' }}>
          <Link to="/shop" className="btn">
            Shop all bestsellers
          </Link>
        </div>
      </section>

      {/* ------------------------------------------------------- science */}
      <section id="science" className="section-split" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,0.78fr) minmax(0,1.6fr)', alignItems: 'stretch', gap: 'clamp(20px,3vw,48px)' }}>
        <div style={{ background: 'var(--oat)', padding: 'clamp(28px,3.4vw,52px)', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 18 }}>
          <h2 className="display" style={{ fontSize: 'clamp(24px,2.5vw,34px)', lineHeight: 1.25, letterSpacing: '.02em' }}>
            Where Science
            <br />
            <span style={{ fontFamily: 'var(--sans)', fontWeight: 500, fontSize: 'clamp(18px,1.9vw,25px)', letterSpacing: '.02em' }}>MEETS BEAUTY.</span>
          </h2>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 300, lineHeight: 1.8, color: '#4b423b', maxWidth: '38ch' }}>
            At Lumera, we combine advanced actives with modern skincare innovation to deliver visible results and a sensorial experience you’ll love every day.
          </p>
          <Link to="/about#philosophy" className="btn btn-sm" style={{ alignSelf: 'flex-start', marginTop: 6 }}>
            Our philosophy
          </Link>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 'clamp(16px,2vw,28px)', padding: 'clamp(28px,3.4vw,52px) clamp(16px,3vw,44px) clamp(28px,3.4vw,52px) 0', alignContent: 'center' }}>
          {PILLARS.map((p) => (
            <div key={p.title} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 14 }}>
              <span style={{ width: 74, height: 74, border: '1px solid #e3c7c7', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#c49b9b', flex: '0 0 auto' }}>
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                  {p.circles && (
                    <>
                      <circle cx="12" cy="12" r="2.4" />
                      <circle cx="5" cy="7" r="1.8" />
                      <circle cx="19" cy="7" r="1.8" />
                      <circle cx="6" cy="18" r="1.8" />
                      <circle cx="18" cy="18" r="1.8" />
                    </>
                  )}
                  <path d={p.path} />
                </svg>
              </span>
              <h3 style={{ margin: 0, fontSize: 10.5, fontWeight: 500, letterSpacing: '.18em', textTransform: 'uppercase' }}>{p.title}</h3>
              <p style={{ margin: 0, fontSize: 12, fontWeight: 300, lineHeight: 1.6, color: 'var(--muted)', maxWidth: '24ch' }}>{p.copy}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ------------------------------------------------------- concern */}
      <section id="concern" className="section">
        <h2 className="rule-head">Shop by concern</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 'clamp(10px,1.4vw,18px)' }}>
          {CONCERN_TILES.map((c) => (
            <Link key={c.concern} to={`/shop?concern=${encodeURIComponent(c.concern)}`} style={{ display: 'flex', flexDirection: 'column', gap: 14, textAlign: 'center' }}>
              <span style={{ position: 'relative', display: 'block', aspectRatio: '1 / 1', overflow: 'hidden' }}>
                <Fill src={`/assets/photos/${c.photo}`} alt={c.alt} label={c.concern} />
              </span>
              <span style={{ fontSize: 10, fontWeight: 400, letterSpacing: '.16em', textTransform: 'uppercase', lineHeight: 1.6, whiteSpace: 'pre-line' }}>{c.label}</span>
            </Link>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 'clamp(22px,2.6vw,34px)' }}>
          <Link to="/quiz" className="btn">
            Find your solution
          </Link>
        </div>
      </section>

      {/* ------------------------------------------------- hero ingredient */}
      <section className="section-split" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', background: 'var(--blush)', minHeight: 'clamp(300px,30vw,420px)' }}>
        <div style={{ position: 'relative', minHeight: 'clamp(240px,28vw,420px)' }}>
          <Fill src="/assets/photos/bubbles-travertine.jpg" alt="Macro of translucent pink spheres suspended in clear gel" label="Exosome texture" />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 18, padding: 'clamp(28px,4vw,64px)' }}>
          <div className="eyebrow-rose">Hero ingredient</div>
          <h2 className="display h-lg">Meet Exosomes.</h2>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 300, lineHeight: 1.8, color: '#4b3c3c', maxWidth: '42ch' }}>
            Advanced skincare technology shown to help support skin regeneration, improve texture and restore a healthy-looking glow.
          </p>
          <Link to="/product/exosome-niacinamide-serum" className="btn" style={{ alignSelf: 'flex-start' }}>
            Discover the formula
          </Link>
        </div>
      </section>

      {/* ------------------------------------------------------- routine */}
      <section id="routine" className="section">
        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 'clamp(24px,3vw,40px)' }}>
          <h2 className="display h-md">The Lumera Routine</h2>
          <p className="small" style={{ margin: 0, letterSpacing: '.06em' }}>
            Simple steps. Powerful results.
          </p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 'clamp(18px,2.4vw,34px)' }}>
          {routineSteps.map((p, i) => (
            <div key={p!.slug} style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', alignItems: 'center', gap: 14 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ fontFamily: 'var(--serif)', fontSize: 40, fontWeight: 300, color: 'var(--rose-pale)', lineHeight: 1 }}>0{i + 1}</div>
                <h3 style={{ margin: 0, fontSize: 13, fontWeight: 500, letterSpacing: '.14em', textTransform: 'uppercase' }}>
                  {p!.step.split('·')[1]?.trim() || p!.category}
                </h3>
                <p style={{ margin: 0, fontSize: 12.5, fontWeight: 300, lineHeight: 1.7, color: 'var(--muted)' }}>
                  {p!.name} — {p!.tagline}
                </p>
              </div>
              <Link to={`/product/${p!.slug}`} style={{ position: 'relative', aspectRatio: '3 / 4', display: 'block', overflow: 'hidden' }}>
                <Fill src={p!.image} alt={p!.name} label={p!.name} />
              </Link>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 'clamp(24px,3vw,38px)' }}>
          <Link to="/quiz" className="btn">
            Build your routine
          </Link>
        </div>
      </section>

      {/* ------------------------------------------------------- reviews */}
      <section style={{ display: 'grid', gridTemplateColumns: 'minmax(260px,1.15fr) repeat(4,minmax(0,1fr))', background: 'var(--oat)' }}>
        <ReviewCarousel />
        {COMMUNITY.map((c) => (
          <Link
            key={c.photo}
            to="/shop"
            aria-label={`Shop the range — ${c.alt}`}
            style={{ position: 'relative', aspectRatio: '4 / 5', display: 'block', overflow: 'hidden' }}
          >
            <Fill src={`/assets/photos/${c.photo}`} alt={c.alt} label="Lumera community" />
          </Link>
        ))}
      </section>

      {/* ------------------------------------------------- for you page */}
      <section className="section">
        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 'clamp(22px,2.6vw,34px)' }}>
          <h2 style={{ margin: 0, fontWeight: 400, fontSize: 12.5, letterSpacing: '.26em', textTransform: 'uppercase' }}>Seen on your For You page</h2>
          <p style={{ margin: 0, fontSize: 12, fontWeight: 300, color: 'var(--muted)' }}>Real results. Real people. Real Lumera.</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 'clamp(10px,1.4vw,18px)' }}>
          {STILLS.map((s, i) => (
            <button
              key={s.photo}
              onClick={() => setPlaying(i)}
              aria-label={s.video ? `Play video ${i + 1}` : `View photo ${i + 1} — ${s.alt}`}
              style={{ position: 'relative', aspectRatio: '3 / 4', border: 'none', padding: 0, background: 'var(--stone)', cursor: 'pointer', overflow: 'hidden' }}
            >
              <Fill src={`/assets/photos/${s.photo}`} alt={s.alt} label="Lumera community" />
              <PlayBadge />
            </button>
          ))}
        </div>
      </section>

      {/* ---------------------------------------------------------- quiz */}
      <section id="quiz" className="section-split" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', background: 'var(--blush)', minHeight: 'clamp(280px,28vw,400px)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 18, padding: 'clamp(28px,4vw,64px)' }}>
          <div className="eyebrow-rose">Not sure where to start?</div>
          <h2 className="display h-lg" style={{ maxWidth: '16ch' }}>
            Take the 60-second skin quiz.
          </h2>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 300, lineHeight: 1.8, color: '#4b3c3c', maxWidth: '34ch' }}>
            We’ll create a personalised routine just for you.
          </p>
          <Link to="/quiz" className="btn btn-solid" style={{ alignSelf: 'flex-start', padding: '14px 30px' }}>
            Start the quiz
          </Link>
        </div>
        <div style={{ position: 'relative', minHeight: 'clamp(220px,26vw,400px)' }}>
          <Fill src="/assets/photos/jar-bedside.jpg" alt="A white cream jar on a bedside surface in low, warm light" label="Cream swatch" />
        </div>
      </section>

      {/* ------------------------------------------------------- journal */}
      <section id="journal" className="section">
        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 'clamp(24px,3vw,38px)' }}>
          <h2 className="display h-md">The Lumera Journal</h2>
          <p className="small" style={{ margin: 0 }}>
            Skincare advice. Ingredient education. Beauty rituals.
          </p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 'clamp(12px,1.6vw,22px)' }}>
          {(posts ?? []).slice(0, 4).map((post) => (
            <Link key={post.slug} to={`/journal/${post.slug}`} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <span style={{ position: 'relative', display: 'block', aspectRatio: '4 / 3', overflow: 'hidden' }}>
                <Fill src={post.image} alt={post.title} label={post.category} />
              </span>
              <span style={{ fontSize: 9.5, letterSpacing: '.2em', textTransform: 'uppercase', color: 'var(--faint)' }}>{post.category}</span>
              <span style={{ fontSize: 13, fontWeight: 400, lineHeight: 1.55 }}>{post.title}</span>
              <span style={{ fontSize: 11, letterSpacing: '.1em', color: 'var(--muted)' }}>Read more →</span>
            </Link>
          ))}
        </div>
      </section>

      {playing !== null && <StillLightbox still={STILLS[playing]} onClose={() => setPlaying(null)} />}
    </Shell>
  )
}

export { Img }
